<template>
  <div>
    <b-row class="align-items-center">
      <b-col md="8">
        <div class="picker-wrap">
          <div class="picker-display" @click="togglePicker">
            <span class="time-value">{{ formattingTime }}</span>
            <i class="icon-outline_timer clock-icon"></i>
          </div>

          <transition name="slide">
            <div v-if="open" class="picker-panel">
              <div class="columns">
                <!-- Hours column -->
                <div class="col">
                  <div class="col-label">{{ $t('global.time.hours') }}</div>
                  <div class="scroll-col" ref="hrsCol" @scroll="onHrsScroll">
                    <div class="scroll-padding" />
                    <div
                      v-for="h in hoursList"
                      :key="h"
                      class="scroll-item"
                      :class="{ active: hours === h }"
                      @click="hours = h"
                    >
                      {{ pad(h, 2) }}
                    </div>
                    <div class="scroll-padding" />
                  </div>
                </div>

                <!-- Minutes column -->
                <div class="col">
                  <div class="col-label">{{ $t('global.time.minutes') }}</div>
                  <div class="scroll-col" ref="minCol" @scroll="onMinScroll">
                    <div class="scroll-padding" />
                    <div
                      v-for="m in minutesList"
                      :key="m"
                      class="scroll-item"
                      :class="{ active: minutes === m }"
                      @click="minutes = m"
                    >
                      {{ pad(m, 2) }}
                    </div>
                    <div class="scroll-padding" />
                  </div>
                </div>
              </div>

              <div class="picker-footer">
                <button class="btn-clear" @click="cancel">{{ $t('global.action.cancel') }}</button>
                <button class="btn-done" @click="submit">{{ $t('global.action.apply') }}</button>
              </div>
            </div>
          </transition>
        </div>
      </b-col>
      <b-col>
        <b-form-radio v-model="type" value="in" class="me-2">
          <label class="radio-label">{{ $t('report.object.onLimit') }}</label>
        </b-form-radio>
        <b-form-radio v-model="type" value="out" class="me-2">
          <label class="radio-label">{{ $t('report.object.exceedLimit') }}</label>
        </b-form-radio>
      </b-col>
    </b-row>
  </div>
</template>

<script>
export default {
  name: 'TimePicker',
  emits: ['update:modelValue', 'update:toggle'],
  data() {
    return {
      open: false,
      oldHours: 0,
      oldMinutes: 0,
      hours: 0,
      minutes: 0,
      type: null,
    }
  },
  watch: {
    open(v) {
      if (v) this.$nextTick(() => this.syncScroll())
    },
    type(v) {
      this.$emit('update:modelValue', { hours: this.hours, minutes: this.minutes, type: v })
    },
  },
  computed: {
    hoursList() {
      return Array.from({ length: 100 }, (_, i) => i) // [0, 1, 2, ... 99]
    },
    minutesList() {
      return Array.from({ length: 60 }, (_, i) => i) // [0, 1, 2, ... 59]
    },
    formattingTime() {
      return `${this.hours} ${this.$t('global.time.hours')} ${this.minutes} ${this.$t(
        'global.time.minutes'
      )}`
    },
  },
  methods: {
    togglePicker() {
      this.open = !this.open
      this.$emit('update:toggle', this.open)
    },
    Hide() {
      this.open = false
    },
    SetTime(time) {
      this.hours = time?.hours ?? 0
      this.minutes = time?.minutes ?? 0
      this.type = time?.type ?? null
      this.oldHours = this.hours
      this.oldMinutes = this.minutes
    },
    pad(n, len) {
      return String(n).padStart(len, '0')
    },
    cancel() {
      this.hours = this.oldHours
      this.minutes = this.oldMinutes
      this.close()
    },
    submit() {
      this.oldHours = this.hours
      this.oldMinutes = this.minutes
      this.close()
    },
    close() {
      this.open = false
      this.$emit('update:modelValue', { hours: this.hours, minutes: this.minutes, type: this.type })
    },
    syncScroll() {
      const ITEM_H = 44
      this._syncing = true
      if (this.$refs.hrsCol) this.$refs.hrsCol.scrollTop = this.hours * ITEM_H
      if (this.$refs.minCol) this.$refs.minCol.scrollTop = this.minutes * ITEM_H
      // Release lock after scroll settles
      clearTimeout(this._syncTimer)
      this._syncTimer = setTimeout(() => {
        this._syncing = false
      }, 300)
    },
    onHrsScroll(e) {
      if (this._syncing) return
      const ITEM_H = 44
      clearTimeout(this._hrsTimer)
      this._hrsTimer = setTimeout(() => {
        const idx = Math.round(e.target.scrollTop / ITEM_H)
        this.hours = Math.min(100, Math.max(0, idx))
        this._syncing = true
        e.target.scrollTop = this.hours * ITEM_H
        setTimeout(() => {
          this._syncing = false
        }, 150)
      }, 120)
    },
    onMinScroll(e) {
      if (this._syncing) return
      const ITEM_H = 44
      clearTimeout(this._minTimer)
      this._minTimer = setTimeout(() => {
        const idx = Math.round(e.target.scrollTop / ITEM_H)
        this.minutes = Math.min(59, Math.max(0, idx))
        this._syncing = true
        e.target.scrollTop = this.minutes * ITEM_H
        setTimeout(() => {
          this._syncing = false
        }, 150)
      }, 120)
    },
  },
}
</script>

<style lang="scss" scoped>
* {
  box-sizing: border-box;
}

.picker-wrap {
  font-family: 'Inter', sans-serif;
  position: relative;
  width: 100%;
}

.picker-label {
  font-weight: 500;
  margin-bottom: 6px;
  color: #9ca3af;
}

.picker-display {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  background: #ffffff;
  border: 1.5px solid #e5e7eb;
  border-radius: 10px;
  padding: 11px 14px;
  cursor: pointer;
  transition: border-color 0.2s, box-shadow 0.2s;
  user-select: none;
}
.picker-display:hover {
  border-color: $primary;
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.08);
}

.clock-icon {
  color: #9ca3af;
}

.time-value {
  letter-spacing: 0.04em;
  flex: 1;
}

.picker-unit {
  color: #9ca3af;
  font-weight: 400;
}

.chevron {
  width: 16px;
  height: 16px;
  color: #9ca3af;
  transition: transform 0.2s ease;
  flex-shrink: 0;
}
.chevron.rotated {
  transform: rotate(180deg);
}

/* Panel */
.picker-panel {
  width: 80%;
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  right: 0;
  z-index: 999;
  background: #ffffff;
  border: 1.5px solid #e5e7eb;
  border-radius: 12px;
  padding: 10px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08), 0 2px 8px rgba(0, 0, 0, 0.04);
}

.columns {
  display: flex;
  align-items: center;
  gap: 8px;
}

.col {
  flex: 1;
}

.col-label {
  font-weight: 500;
  letter-spacing: 0.08em;
  color: #9ca3af;
  text-align: center;
  margin-bottom: 8px;
}

.colon {
  font-weight: 600;
  color: $primary;
  margin-top: 4px;
  align-self: center;
  padding: 0 2px;
}

.scroll-col {
  height: 220px;
  overflow-y: scroll;
  scroll-snap-type: y mandatory;
  scrollbar-width: none;
  position: relative;
  border-radius: 8px;
  background: #f9fafb;
  border: 1px solid #f3f4f6;
}
.scroll-col::-webkit-scrollbar {
  display: none;
}

.scroll-col::before,
.scroll-col::after {
  content: '';
  position: sticky;
  display: block;
  left: 0;
  right: 0;
  height: 44px;
  pointer-events: none;
  z-index: 2;
}
.scroll-col::before {
  top: 0;
  background: linear-gradient(to bottom, #f9fafb, transparent);
}
.scroll-col::after {
  bottom: 0;
  background: linear-gradient(to top, #f9fafb, transparent);
}

.scroll-padding {
  height: 50px;
  flex-shrink: 0;
}

.scroll-item {
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #d1d5db;
  scroll-snap-align: center;
  cursor: pointer;
  transition: color 0.15s;
  border-radius: 6px;
  margin: 0 4px;
}
.scroll-item:hover {
  color: #6b7280;
}
.scroll-item.active {
  color: $primary;
  background: rgba(99, 102, 241, 0.08);
}

.picker-footer {
  display: flex;
  gap: 8px;
  margin-top: 14px;
}

button {
  flex: 1;
  padding: 9px 0;
  border: none;
  border-radius: 8px;
  font-family: 'Inter', sans-serif;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: opacity 0.15s;
}
button:hover {
  opacity: 0.8;
}

.btn-clear {
  background: #f3f4f6;
  color: #6b7280;
}
.btn-done {
  background: $primary;
  color: #fff;
}

/* Transition */
.slide-enter-active,
.slide-leave-active {
  transition: all 0.18s cubic-bezier(0.16, 1, 0.3, 1);
}
.slide-enter-from,
.slide-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}
</style>
